import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { API_BASE_URL } from "../config/api";

/**
 * Base query with error handling for authentication
 * Uses HTTP-only cookies for authentication
 */
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: "include", // Include cookies in requests
  prepareHeaders: (headers, { getState: _getState }) => {
    // Get session ID for cart operations (still using localStorage for this)
    const sessionId = localStorage.getItem("sessionId");
    if (sessionId) {
      headers.set("x-session-id", sessionId);
    }
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

/**
 * Base query with automatic token invalidation on 401
 */
const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // If we get a 401, the token is invalid
  if (result.error && result.error.status === 401) {
    // Get the endpoint URL being called
    const url = typeof args === "string" ? args : args.url;
    const currentPath = window.location.pathname;

    // Don't redirect for:
    // 1. Auth-related endpoints (profile check, login, etc.) - expected to return 401
    // 2. Public endpoints (cart, checkout, public settings, etc.)
    // 3. When already on login page
    const isAuthEndpoint = url?.includes("/auth/");
    const isPublicEndpoint =
      url?.includes("/cart") ||
      url?.includes("/settings/public") ||
      url?.includes("/payments/providers") ||
      url?.includes("/products") ||
      url?.includes("/categories");
    const isPublicPage =
      currentPath === "/cart" ||
      currentPath === "/checkout" ||
      currentPath.startsWith("/product/") ||
      currentPath.startsWith("/our-menu") ||
      currentPath === "/" ||
      currentPath.startsWith("/payment/callback");

    // Only redirect if we're not on a public page, not calling a public endpoint, 
    // not on the login page, and it's not an auth endpoint
    if (!isAuthEndpoint && !isPublicEndpoint && !isPublicPage && currentPath !== "/admin/login") {
      window.location.href = "/admin/login";
    }
  }

  return result;
};

/**
 * Base API slice with RTK Query
 * This serves as the foundation for all API endpoints
 */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Product", "Category", "Order", "Cart", "User", "Auth", "Admin", "Customer", "Transaction", "ServiceRequest", "Media", "FAQ", "FAQCategory", "Analytics", "Stock", "StockSettings", "Settings", "PublicSettings", "Testimonial", "Deal", "Newsletter", "Coupon"],
  endpoints: () => ({}),
});

